import isString from "lodash/isString";
import dayjs from "dayjs";
import { IResult } from "ua-parser-js";
import { COUNTRIES } from "./phoneUtils/countries";

// Country codes the CountryCodeSelect UI can actually render (drawn from the COUNTRIES list),
// longest-first so the most specific code wins when one is a prefix of another.
const DISPLAY_COUNTRY_CODES = [...new Set(COUNTRIES.map((country) => country.code))].sort((a, b) => b.length - a.length);

export const getPhoneCountryCode = (value: string) => {
  return DISPLAY_COUNTRY_CODES.find((code) => value.startsWith(code));
};

export const updateDayjsGlobalLocale = (userLocale: string | undefined) => {
  if (userLocale) {
    const setValue = dayjs.locale(userLocale);
    if (setValue !== userLocale) {
      dayjs.locale(userLocale.split("-")[0]);
    }
  }
};

export const removeNullProps = (obj: Record<string, any>): any => Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null));

/**
 * `<img>` in any form, plus `<image>` — browsers treat the latter as an alias, so a blocklist that
 * only names `img` is trivially stepped around.
 *
 * A closing `>` is required so prose like "attach the <img in the export" keeps its tail. An
 * unclosed `<img ...` therefore survives, but its `onerror=` has already been taken by the
 * event-handler rule below, so what is left cannot fire.
 */
const IMG_TAG = /<\/?(?:image|img)\b[^>]*>/gi;

/**
 * Any complete tag, used to confine the attribute rules below to tag context.
 *
 * Quoted attribute values are consumed as units, because a naive `<[^>]*>` ends at the first `>`
 * even when that `>` sits inside a quoted value — so `<a title=">x" href="&#106;avascript:alert(1)">`
 * would have been "scanned" as `<a title=">` and the href never examined at all. A browser reads the
 * quoted `>` as attribute content and honours the href, so that gap was a live bypass of every rule
 * this regex gates.
 */
const ANY_TAG = /<(?:"[^"]*"|'[^']*'|[^>"'])*>/g;

/**
 * Tags that fetch, navigate, execute or rewrite the page without needing an event handler, so the
 * `on<name>=` rule never sees them. None of them has any business in a CRM text field, and dropping
 * them is what closes `<iframe srcdoc>` — whose decoded payload carries neither a handler nor a
 * `javascript:` scheme and so slips past DANGEROUS_ONCE_DECODED.
 *
 * `<script>` and `<style>` are removed WITH their contents, otherwise the code inside them would be
 * left rendering as visible text.
 */
const DANGEROUS_TAG_WITH_CONTENT = /<(script|style)\b[^>]*>[\s\S]*?(?:<\/\1\s*>|$)/gi;
const DANGEROUS_TAG = /<\/?(?:iframe|object|embed|base|meta|link|form|applet|frame|frameset|script|style)\b[^>]*>?/gi;

/**
 * Unquoted `src=value`. The quoted `src="..."` / `src='...'` forms are handled inside `stripXSSstr`;
 * this is the bare form the August 2026 pen-test payload used (`<img src=x onerror=...>`).
 *
 * Only applied INSIDE a tag, so a bare `?src=avatar` in a URL is left alone.
 */
const UNQUOTED_SRC_IN_TAG = /\bsrc[ \t\r\n]*=[ \t\r\n]*[^\s>'"]+/gi;

/**
 * Any `on<name>=` attribute, confined to tag context.
 *
 * This supersedes naming handlers one at a time: the named list below covers 21, the DOM has well
 * over 100, and every addition bought one name rather than one class. Confining it to tags is what
 * makes a rule this broad safe — an unanchored version would also eat prose like "one=1".
 */
const EVENT_HANDLER_IN_TAG = /\bon[a-z][a-z0-9_-]*[ \t\r\n]*=/gi;

/**
 * What must not survive inside a tag once a browser has resolved character references.
 * `\s` is deliberate: browsers ignore whitespace and control characters inside a scheme, so
 * `java&#9;script:` reaches the parser as `javascript:`.
 */
const DANGEROUS_ONCE_DECODED =
  /\bon[a-z][a-z0-9_-]*[ \t\r\n]*=|\bsrcdoc[ \t\r\n]*=|j\s*a\s*v\s*a\s*s\s*c\s*r\s*i\s*p\s*t\s*:|v\s*b\s*s\s*c\s*r\s*i\s*p\s*t\s*:/i;

const NAMED_REFS: Record<string, string> = { quot: '"', apos: "'", lt: "<", gt: ">", amp: "&", tab: "\t", newline: "\n", colon: ":" };

/**
 * Resolves character references the way a browser would — FOR DETECTION ONLY, never for output.
 *
 * Decoding in place would be a vulnerability in its own right: `title="&quot;onx=1"` would decode to
 * a real quote and split one attribute into two, manufacturing the injection point. So the decoded
 * form is only ever tested, and a tag that is dangerous only once decoded is dropped whole.
 */
const decodeRefsForDetection = (tag: string): string =>
  tag
    .replace(/&#x([0-9a-f]+);?/gi, (match, hex) => codePointOrMatch(parseInt(hex, 16), match))
    .replace(/&#(\d+);?/g, (match, dec) => codePointOrMatch(parseInt(dec, 10), match))
    .replace(/&([a-z]+);/gi, (match, name) => NAMED_REFS[name.toLowerCase()] ?? match);

const codePointOrMatch = (code: number, match: string): string => {
  if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) {
    return match;
  }
  try {
    return String.fromCodePoint(code);
  } catch {
    return match;
  }
};

export const stripXSSstr = (str: string) => {
  let value = str;

  // Avoid null characters
  value = value.replaceAll("", "");
  // Avoid anything between script tags
  value = value.replaceAll(new RegExp("<script>(.*?)</script>", "ig"), "");
  // Avoid anything in a src='...' type of expression
  value = value.replaceAll(
    // eslint-disable-next-line no-control-regex
    new RegExp("src[\r\n]*=[\r\n]*\\'(.*?)\\'", "imsg"),
    ""
  );
  value = value.replaceAll(
    // eslint-disable-next-line no-control-regex
    new RegExp('src[\r\n]*=[\r\n]*\\"(.*?)\\"', "imsg"),
    ""
  );
  // Remove any lonesome </script> tag
  value = value.replaceAll(new RegExp("</script>", "imsg"), "");

  // Remove any lonesome <script ...> tag
  value = value.replaceAll(new RegExp("<script(.*?)>", "imsg"), "");

  // Avoid eval(...) expressions
  value = value.replaceAll(new RegExp("eval\\((.*?)\\)", "imsg"), "");

  // Avoid expression(...) expressions
  value = value.replaceAll(new RegExp("expression\\((.*?)\\)", "imsg"), "");

  // Avoid javascript:... expressions
  value = value.replaceAll(new RegExp("javascript:", "ig"), "");

  // Avoid vbscript:... expressions
  value = value.replaceAll(new RegExp("vbscript:", "ig"), "");

  /* Avoid inline js expressions
           onchange
           onblur
           onfocus
           onselect
           onsubmit
           onreset
           onkeydown
           onkeypress
           onkeyup
           onkeydown
           onload
           onclick
           onmouseout
           onmouseover
           onmouseup
           onmousedown
           onmousemove
           ondblink
           onerror
           onunload
           onresize
            */
  value = value.replaceAll(
    new RegExp(
      "onchange(.*?)=|onblur(.*?)=|onfocus(.*?)=|onselect(.*?)=|onsubmit(.*?)=|onreset(.*?)=|onkeydown(.*?)=|onkeypress(.*?)=|onkeyup(.*?)=|onkeydown(.*?)=|onload(.*?)=|onclick(.*?)=|onmouseout(.*?)=|onmouseover(.*?)=|onmouseup(.*?)=|onmousedown(.*?)=|onmousemove(.*?)=|ondblink(.*?)=|onerror(.*?)=|onunload(.*?)=|onresize(.*?)=",
      "imsg"
    ),
    ""
  );

  // Strip `<img>`/`<image>` outright [EN-7291]. Removing the whole tag — rather than only its
  // `onerror=` — is what keeps a neutralised payload from rendering as visible garbage text.
  value = value.replace(IMG_TAG, "");

  // Then the tags that act without an event handler at all: script/style go with their contents so
  // the code inside them is not left as visible text, and the rest go as tags.
  value = value.replace(DANGEROUS_TAG_WITH_CONTENT, "").replace(DANGEROUS_TAG, "");

  // Finally clean what is left of every tag: any `on<name>=` handler and any unquoted `src=`, then
  // re-test with character references resolved — a payload that survives only because it was encoded
  // (`&#106;avascript:`) cannot be edited out safely, so the whole tag goes.
  value = value.replace(ANY_TAG, (tag) => {
    const cleaned = tag.replace(EVENT_HANDLER_IN_TAG, "").replace(UNQUOTED_SRC_IN_TAG, "");
    return DANGEROUS_ONCE_DECODED.test(decodeRefsForDetection(cleaned)) ? "" : cleaned;
  });

  return value;
};

export const assertStrXssFree = (str: string) => {
  const noXss = stripXSSstr(str);
  return noXss === str;
};

const isPrimitiveValue = (value: any): boolean => {
  return (typeof value !== "object" && typeof value !== "function") || value === null;
};

export const makeSerializableError = (error: Error) => {
  if (isPrimitiveValue(error)) {
    return error;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const serializable: any = {};

  Object.getOwnPropertyNames(error)
    .map((key) => ({ key, value: error[key as keyof Error] }))
    .filter(({ value }) => isPrimitiveValue(value))
    .forEach(({ key, value }) => (serializable[key] = value));

  return serializable;
};

export const randomString = (length: number) => {
  return Array.from(Array(length), () => Math.floor(Math.random() * 36).toString(36)).join("");
};

export const delay = (ms: number) => {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
};

export const retry = async <T>(
  fn: (signal?: AbortSignal) => T,
  {
    maxRetry = 5,
    delayBetweenRetryMs = 0,
    signal,
    backoff,
    backoffExtraToDelay = 0,
    shouldRetry,
  }: {
    maxRetry?: number;
    delayBetweenRetryMs?: number;
    signal?: AbortSignal;
    backoff?: boolean;
    backoffExtraToDelay?: number;
    shouldRetry?: (err: unknown) => boolean;
  },
  currentRetry = 0
): Promise<T> => {
  if (signal?.aborted) {
    throw signal.reason;
  }

  if (maxRetry === 0) {
    throw new Error("failed max retry");
  }
  try {
    return await fn(signal);
  } catch (err) {
    if (shouldRetry && !shouldRetry(err)) {
      throw err;
    }
    if (backoff) {
      const timeToWait = 2 ** currentRetry * 100 + backoffExtraToDelay;
      await delay(timeToWait);
    } else if (delayBetweenRetryMs > 0) {
      await delay(delayBetweenRetryMs);
    }

    return retry(
      fn,
      {
        // eslint-disable-next-line no-useless-assignment
        maxRetry: --maxRetry,
        delayBetweenRetryMs,
        signal,
        backoff,
        backoffExtraToDelay,
        shouldRetry,
      },
      currentRetry + 1
    );
  }
};

export const calFileListSize = (files: File[], sizeLimit = 10 * 1000 * 1000) => {
  let size = 0;
  // eslint-disable-next-line no-useless-assignment
  let type = "";

  files.forEach((file) => {
    if (file.size) {
      size += file.size;
    }
  });

  if (size > sizeLimit) {
    return;
  } else if (size > 999999) {
    size = size / (1000 * 1000);
    type = "MB";
  } else if (size > 999) {
    size = size / 1000;
    type = "KB";
  } else if (size > 0) {
    type = "Bytes";
  } else {
    return "0";
  }
  return size.toFixed() + type;
};

export const calFileListSizeV2 = (files: File[], sizeLimit = 10 * 1000 * 1000) => {
  // eslint-disable-next-line no-useless-assignment
  let type = "";

  let size = files.reduce<number>((sizeToReturn, file) => {
    if (file.size) {
      sizeToReturn += file.size;
    }
    return sizeToReturn;
  }, 0);

  if (size > sizeLimit) {
    return;
  } else if (size > 999999) {
    size = size / (1000 * 1000);
    type = "MB";
  } else if (size > 999) {
    size = size / 1000;
    type = "KB";
  } else if (size > 0) {
    type = "Bytes";
  } else {
    return "0";
  }
  return size.toFixed() + type;
};

export const calFileSizeV2 = (file: { size: number }) => {
  let size = 0;
  let type = "";
  const filesSizeArray: string[] = [];
  if (file.size) {
    size += file.size;
    if (size > 999999) {
      size = size / (1000 * 1000);
      type = "MB";
      filesSizeArray.push(`${file.size / (1000 * 1000)}MB`);
    } else if (size > 999) {
      size = size / 1000;
      type = "KB";
      filesSizeArray.push(`${file.size / 1000}KB`);
    } else if (size > 0) {
      type = "Bytes";
      filesSizeArray.push(`${file.size} Bytes`);
    } else {
      return filesSizeArray.push("0");
    }
  }
  return size.toFixed() + type;
};

export const getPrintableDate = () => {
  const d = new Date(); // 2009-11-10
  const m = d.toLocaleString("default", { month: "short" });
  return `${d.getDate()} ${m}, ${d.getFullYear()}`;
};

export const stripNumberStringToOnlyNumbers = (value: string) => {
  if (!value) {
    return value;
  }
  return value.trim().replaceAll(/[^\d.]/g, "");
};

export class CustomError extends Error {
  data: any;
  constructor(message: any, data: any) {
    super(message);
    this.data = data;
  }
}
export const hidePhoneNumber = (phoneNumber: string) => {
  const onlyNumbers = stripNumberStringToOnlyNumbers(phoneNumber);
  const hiddenDigits = onlyNumbers.slice(0, -4).replace(/\d/g, "*");
  const visibleDigits = onlyNumbers.slice(-4);
  return hiddenDigits + visibleDigits;
};
export const getPrettyPhone = (value: string) => {
  if (!value) {
    return value;
  }
  const onlyNumbers = stripNumberStringToOnlyNumbers(value);
  const code = getPhoneCountryCode(onlyNumbers);
  if (!code) {
    return value;
  }
  const phoneWithoutCodePrefix = onlyNumbers.substring(code.length, value.length);

  if (code.length == 1) {
    return stripXSSstr(`+${code}${phoneWithoutCodePrefix.replace(/(\d{3})(\d{3})(\d+)/, " ($1) $2-$3")}`);
  }
  if (code === "55") {
    if (onlyNumbers.length === 12) {
      return stripXSSstr(`+${code}${phoneWithoutCodePrefix.replace(/(\d{2})(\d{4})(\d+)/, " ($1) $2-$3")}`);
    }
    return stripXSSstr(`+${code}${phoneWithoutCodePrefix.replace(/(\d{2})(\d{5})(\d+)/, " ($1) $2-$3")}`);
  }
  return stripXSSstr(`+${code}${phoneWithoutCodePrefix.replace(/(\d{2})(\d{3})(\d+)/, " ($1) $2-$3")}`);
};

export const dataUriToBase64 = (dataUri: string): { type: string | undefined; base64: string | undefined } => {
  const commaIndex = dataUri.indexOf(",");
  if (!/^data:/i.test(dataUri) || commaIndex === -1) return { type: undefined, base64: undefined };
  const type = dataUri.slice(5, commaIndex).split(";")[0].trim() || undefined;
  const base64 = dataUri.slice(commaIndex + 1) || undefined;

  return { type, base64 };
};

export const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return "0 Bytes";

  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const scaleMetrics = (metrics: number, numOfDigitsAfterThePoint = 0) => {
  if (metrics === 0) {
    return "0";
  }
  if (metrics < 1_000) {
    return String(metrics);
  }
  const sizes = ["", "K", "M", "B", "T"];
  const i = Math.floor(Math.log(metrics) / Math.log(1_000));
  if (i > sizes.length - 1) {
    return metrics.toFixed(numOfDigitsAfterThePoint);
  }
  return `${(metrics / 1_000 ** i).toFixed(numOfDigitsAfterThePoint)}${sizes[i] ?? ""}`;
};

const intl = new Intl.NumberFormat("en-US");
export const numberWithCommas = (value: number) => {
  return intl.format(value);
};

export const getBrowserVersion = (userAgent: IResult) => {
  try {
    const raw = userAgent?.browser?.version?.split(".")[0];
    if (!raw) {
      throw new Error();
    }
    return parseInt(raw as string);
  } catch {
    throw new Error(`Failed get browser version status: failed parse user agent`);
  }
};

export const stringIsNumber = (val: string) => {
  return !Number.isNaN(Number(val));
};

export const toPascalCase = (str: string) => {
  if (!str) return "";
  return str.replace(/(\w)(\w*)/g, function (g0, g1, g2) {
    return g1.toUpperCase() + g2.toLowerCase();
  });
};

export const stringIsPhoneOrNameOrEmail = (str: string) => {
  if (str.length > 1 && str.charAt(0) !== "@" && str.includes("@")) {
    return "email";
  }
  const qWithoutSpecialChars = stripXSSstr(str)
    .replace(/[&/\\#,+()$~%.'":*?<>{}-]/g, "")
    .replace(" ", "");
  const qWithoutNumbers = qWithoutSpecialChars.replace(/\D/g, "");
  if (qWithoutNumbers && !Number.isNaN(Number(qWithoutNumbers))) {
    return "phone";
  }
  return "name";
};

//if input phone (from WAP) is the same as the one returned from SF, I prefer the one from WAP (with country code) - SUP-723 + SUP-380
export const getWapOrSFPhone = (phone: string, selectedContactPhone?: string) => {
  const stringPhone = isString(phone) ? phone : String(phone);

  if (stringPhone && selectedContactPhone && selectedContactPhone.length > 8 && phone.length > 8 && phone.length !== 13) {
    const cleanReturnedPhone = selectedContactPhone.trim().replaceAll(/[^\d.]/g, "");
    if (cleanReturnedPhone.substring(cleanReturnedPhone.length - 5, cleanReturnedPhone.length) === phone.substring(phone.length - 5, phone.length)) {
      return selectedContactPhone;
    }
  }
  return phone;
};

export function maskStringExceptStartEnd(str: string, preserveStart = 2, preserveEnd = 2) {
  if (preserveStart < 0) preserveStart = 0;
  if (preserveEnd < 0) preserveEnd = 0;
  const totalMaskLength = str.length - preserveStart - preserveEnd;
  const maskedPart = "*".repeat(totalMaskLength);
  return str.slice(0, preserveStart) + maskedPart + str.slice(str.length - preserveEnd);
}

export function getStringUntilCharacterOrAll(inputString: string, character: string) {
  return inputString.includes(character) && !inputString.includes("rc") ? inputString.split(character)[0] : inputString;
}

export function stringToColor(str: string = "") {
  const hash = str.split("").reduce((acc, char) => char.charCodeAt(0) + ((acc << 5) - acc), 0);
  return `hsl(${hash % 360}, 50%, 70%)`;
}

export function isValidSalesforceOrgId(orgId: string) {
  const regex = /^[a-zA-Z0-9]{15,18}$/;
  return regex.test(orgId);
}

export const isValidSalesforceId = (id: string): boolean => {
  // Salesforce IDs are either 15 or 18 characters long
  if (id.length !== 15 && id.length !== 18) {
    return false;
  }

  // Check if the ID consists of only alphanumeric characters
  const alphanumericRegex = /^[a-zA-Z0-9]+$/;
  if (!alphanumericRegex.test(id)) {
    return false;
  }

  // For 18-character IDs, the last 3 characters should be uppercase
  if (id.length === 18) {
    const lastThreeChars = id.slice(-3);
    if (lastThreeChars !== lastThreeChars.toUpperCase()) {
      return false;
    }
  }

  return true;
};

export const AWS_REGION_LIST = [
  "us-east-2",
  "us-east-1",
  "us-west-1",
  "us-west-2",
  "af-south-1",
  "ap-east-1",
  "ap-south-2",
  "ap-southeast-3",
  "ap-southeast-4",
  "ap-south-1",
  "ap-northeast-3",
  "ap-northeast-2",
  "ap-southeast-1",
  "ap-southeast-2",
  "ap-northeast-1",
  "ca-central-1",
  "eu-central-1",
  "eu-west-1",
  "eu-west-2",
  "eu-south-1",
  "eu-west-3",
  "eu-south-2",
  "eu-north-1",
  "eu-central-2",
  "il-central-1",
  "me-south-1",
  "me-central-1",
  "sa-east-1",
  "us-gov-east-1",
  "us-gov-west-1",
] as const;

export function trimObjectStrings<T>(arr: T[]): T[] {
  return arr.map((obj) => {
    const newObj: T = { ...obj };
    for (const key in newObj) {
      if (typeof newObj[key] === "string") {
        (newObj[key] as string) = (newObj[key] as string).trim();
      }
    }
    return newObj;
  });
}
